/**
 * Helpers for a US NANP-style phone field: user types digits only; display shows (555) 123-4567.
 */

const MAX_US_DIGITS = 10;

/**
 * Extracts digits for US phone input. Strips non-digits, removes a leading country code `1` when
 * the result would be 11 digits, and caps at 10 national digits.
 *
 * @param raw - Value from the input (may include mask punctuation).
 * @returns Up to 10 digits, no separators.
 *
 * @example
 * normalizeUsPhoneDigits('(555) 123-4567') // '5551234567'
 */
export function normalizeUsPhoneDigits(raw: string): string {
  let digits = raw.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) {
    digits = digits.slice(1);
  }
  return digits.slice(0, MAX_US_DIGITS);
}

/**
 * Formats national digits as `(555) 123-4567` for display while typing.
 *
 * @param raw - Raw input string (formatted or not).
 * @returns Masked string for controlled input `value`.
 *
 * @example
 * formatUsPhoneMask('555123') // '(555) 123'
 */
export function formatUsPhoneMask(raw: string): string {
  const d = normalizeUsPhoneDigits(raw);
  if (d.length === 0) return '';
  if (d.length <= 3) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}
